/**
 * HealthLog — calm parent + staff health & medication log per child.
 *
 * Parent: can add/stop medications and care notes.
 * Staff (caregiver/admin): can mark a medication administered + add care notes.
 *
 * Privacy: only the child's parents, assigned caregivers, and workspace admins
 * see this page's data. Other parents in the same workspace never see another
 * family's medications.
 */
import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { useChild } from "../lib/childContext";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import Brand from "../components/Brand";
import { ArrowLeft, Pill, Heart, Trash2, CheckCircle2 } from "lucide-react";

const FREQUENCY_LABELS = {
  once: "Once",
  as_needed: "As needed",
  daily: "Once daily",
  twice_daily: "Twice daily",
  every_4h: "Every 4 hours",
  every_6h: "Every 6 hours",
  every_8h: "Every 8 hours",
};

const UNITS = ["mg", "ml", "drops", "puff", "tablet", "spray"];
const FREQUENCIES = Object.keys(FREQUENCY_LABELS);

function timeAgo(iso) {
  if (!iso) return "";
  const now = Date.now();
  const t = new Date(iso).getTime();
  const min = Math.floor((now - t) / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function HealthLog() {
  const { user } = useAuth();
  const { selected } = useChild();
  const navigate = useNavigate();
  const { t } = useI18n();

  const [summary, setSummary] = useState(null);
  const [showAddMed, setShowAddMed] = useState(false);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [busy, setBusy] = useState(false);

  const isParent = user?.role === "parent";
  const isStaff = user?.role === "caregiver" || user?.role === "admin";

  const fetchSummary = useCallback(async () => {
    if (!selected?.id) return;
    try {
      const r = await api.get(`/children/${selected.id}/health-summary`);
      setSummary(r.data);
    } catch (_e) {
      setSummary(null);
    }
  }, [selected?.id]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  if (!selected) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#525252]">
        Please choose a child first.
      </div>
    );
  }

  return (
    <div data-testid="health-screen" className="min-h-screen">
      <header className="px-5 sm:px-10 pt-6 flex items-center justify-between">
        <button
          type="button"
          onClick={() => navigate("/more")}
          data-testid="health-back"
          className="flex items-center gap-2 text-[#6A6863] hover:text-[#2C2B29]"
          aria-label={t("common.back")}
        >
          <ArrowLeft size={18} strokeWidth={1.5} />
          <span className="text-sm">More</span>
        </button>
        <Brand size="sm" dataTestId="brand-health" />
      </header>

      <main className="px-6 sm:px-10 pt-8 pb-32 max-w-2xl mx-auto fade-up">
        <span className="eyebrow">{selected.name}</span>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl tracking-tight leading-none">
          {t("health.title")}
        </h1>
        <p className="mt-4 text-[#525252] text-sm">{t("health.subtitle")}</p>

        {/* Active medications */}
        <section className="mt-10">
          <div className="flex items-center justify-between">
            <span className="eyebrow flex items-center gap-2">
              <Pill size={14} strokeWidth={1.5} /> {t("health.eyebrow.meds")}
            </span>
            {isParent && (
              <button
                type="button"
                data-testid="health-add-med"
                onClick={() => setShowAddMed(true)}
                className="text-sm text-[#1A1B2E] underline underline-offset-4"
              >
                {t("health.add_med")}
              </button>
            )}
          </div>

          {!summary ? (
            <div className="mt-6 text-[#A3A3A3] text-sm">{t("common.loading")}</div>
          ) : summary.active_medications.length === 0 ? (
            <p
              data-testid="health-meds-empty"
              className="mt-6 text-[#525252] text-sm italic"
            >
              {t("health.empty_meds")}
            </p>
          ) : (
            <ul
              data-testid="health-meds-list"
              className="mt-4 rounded-3xl bg-white border hairline overflow-hidden divide-y divide-[#171717]/6"
            >
              {summary.active_medications.map((m) => (
                <li
                  key={m.id}
                  data-testid={`health-med-${m.id}`}
                  className="px-5 py-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-medium text-[#171717]">{m.name}</div>
                      <div className="text-sm text-[#525252] mt-0.5">
                        {m.dose_amount} {m.dose_unit} ·{" "}
                        {FREQUENCY_LABELS[m.frequency] || m.frequency}
                      </div>
                      {m.reason && (
                        <div className="text-xs text-[#6A6863] italic mt-1">
                          {m.reason}
                        </div>
                      )}
                      {m.last_administered_at && (
                        <div className="text-xs text-[#8A9984] mt-2">
                          Last given {timeAgo(m.last_administered_at)} ·{" "}
                          {m.administrations_count}x total
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                      {isStaff && (
                        <button
                          type="button"
                          disabled={busy}
                          data-testid={`health-administer-${m.id}`}
                          onClick={async () => {
                            setBusy(true);
                            try {
                              await api.post(`/medications/${m.id}/administer`, {});
                              toast.success(t("health.administered_just_now"));
                              await fetchSummary();
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || t("common.error"));
                            } finally {
                              setBusy(false);
                            }
                          }}
                          className="text-xs px-3 py-1.5 rounded-full bg-[#132619] text-white inline-flex items-center gap-1.5"
                        >
                          <CheckCircle2 size={13} strokeWidth={1.7} />
                          {t("health.action.administer")}
                        </button>
                      )}
                      {isParent && (
                        <button
                          type="button"
                          disabled={busy}
                          data-testid={`health-stop-${m.id}`}
                          onClick={async () => {
                            if (!window.confirm(t("health.action.stop") + "?")) return;
                            setBusy(true);
                            try {
                              await api.delete(`/medications/${m.id}`);
                              toast.success(t("health.action.stop"));
                              await fetchSummary();
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || t("common.error"));
                            } finally {
                              setBusy(false);
                            }
                          }}
                          className="text-xs text-[#A33B3B] inline-flex items-center gap-1"
                        >
                          <Trash2 size={12} strokeWidth={1.7} /> {t("health.action.stop")}
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Care notes */}
        <section className="mt-12">
          <div className="flex items-center justify-between">
            <span className="eyebrow flex items-center gap-2">
              <Heart size={14} strokeWidth={1.5} /> {t("health.eyebrow.events")}
            </span>
            <button
              type="button"
              data-testid="health-add-event"
              onClick={() => setShowAddEvent(true)}
              className="text-sm text-[#1A1B2E] underline underline-offset-4"
            >
              {t("health.add_event")}
            </button>
          </div>

          {!summary ? null : summary.recent_events.length === 0 ? (
            <p
              data-testid="health-events-empty"
              className="mt-6 text-[#525252] text-sm italic"
            >
              {t("health.empty_events")}
            </p>
          ) : (
            <ul
              data-testid="health-events-list"
              className="mt-4 rounded-3xl bg-white border hairline overflow-hidden divide-y divide-[#171717]/6"
            >
              {summary.recent_events.map((e) => (
                <li key={e.id} data-testid={`health-event-${e.id}`} className="px-5 py-4">
                  <div className="text-xs uppercase tracking-wide text-[#8A9984]">
                    {e.kind.replace("_", " ")}
                  </div>
                  <div className="font-medium text-[#171717] mt-0.5">{e.summary}</div>
                  {e.body && (
                    <div className="text-sm text-[#525252] mt-1">{e.body}</div>
                  )}
                  <div className="text-xs text-[#6A6863] mt-2 italic">
                    by {e.created_by_name} · {timeAgo(e.occurred_at)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Administration history */}
        {summary && summary.recent_administrations.length > 0 && (
          <section className="mt-12">
            <span className="eyebrow">{t("health.eyebrow.history")}</span>
            <ul
              data-testid="health-history-list"
              className="mt-4 rounded-3xl bg-[#F2EFEA]/60 border hairline overflow-hidden divide-y divide-[#171717]/6"
            >
              {summary.recent_administrations.slice(0, 8).map((a) => (
                <li key={a.id} className="px-5 py-3">
                  <div className="text-sm text-[#2C2B29]">
                    <span className="font-medium">{a.medication_name}</span>{" "}
                    <span className="text-[#6A6863]">
                      ({a.dose_amount} {a.dose_unit})
                    </span>
                  </div>
                  <div className="text-xs text-[#8A9984] mt-1">
                    by {a.administered_by_name} · {timeAgo(a.at)}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {isParent && (
          <p className="mt-12 text-xs text-[#8A9984] italic">{t("health.parent_only")}</p>
        )}
      </main>

      {showAddMed && (
        <AddMedicationModal
          childId={selected.id}
          onClose={() => setShowAddMed(false)}
          onSaved={async () => {
            setShowAddMed(false);
            await fetchSummary();
          }}
        />
      )}
      {showAddEvent && (
        <AddEventModal
          childId={selected.id}
          onClose={() => setShowAddEvent(false)}
          onSaved={async () => {
            setShowAddEvent(false);
            await fetchSummary();
          }}
        />
      )}
    </div>
  );
}

function AddMedicationModal({ childId, onClose, onSaved }) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    name: "",
    dose_amount: "",
    dose_unit: "mg",
    frequency: "as_needed",
    reason: "",
    start_date: new Date().toISOString().slice(0, 10),
    end_date: "",
    consent_text: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        dose_amount: parseFloat(form.dose_amount),
        end_date: form.end_date || null,
        reason: form.reason || null,
      };
      await api.post(`/children/${childId}/medications`, payload);
      toast.success(t("common.save"));
      await onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      data-testid="health-add-med-modal"
      className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-end sm:items-center justify-center px-4"
    >
      <form
        onSubmit={submit}
        className="bg-white rounded-3xl w-full max-w-md p-6 space-y-3"
      >
        <h3 className="font-serif text-2xl text-[#171717]">{t("health.add_med")}</h3>
        <Field label={t("health.field.name")}>
          <input
            data-testid="health-add-med-name"
            required
            maxLength={80}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="input-soft w-full"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("health.field.dose")}>
            <input
              data-testid="health-add-med-dose"
              required
              type="number"
              step="0.01"
              min="0.01"
              value={form.dose_amount}
              onChange={(e) => setForm({ ...form, dose_amount: e.target.value })}
              className="input-soft w-full"
            />
          </Field>
          <Field label={t("health.field.unit")}>
            <select
              data-testid="health-add-med-unit"
              value={form.dose_unit}
              onChange={(e) => setForm({ ...form, dose_unit: e.target.value })}
              className="input-soft w-full"
            >
              {UNITS.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label={t("health.field.frequency")}>
          <select
            data-testid="health-add-med-freq"
            value={form.frequency}
            onChange={(e) => setForm({ ...form, frequency: e.target.value })}
            className="input-soft w-full"
          >
            {FREQUENCIES.map((f) => (
              <option key={f} value={f}>
                {FREQUENCY_LABELS[f]}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("health.field.reason")}>
          <input
            data-testid="health-add-med-reason"
            maxLength={240}
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
            className="input-soft w-full"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("health.field.start")}>
            <input
              data-testid="health-add-med-start"
              required
              type="date"
              value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              className="input-soft w-full"
            />
          </Field>
          <Field label={t("health.field.end")}>
            <input
              data-testid="health-add-med-end"
              type="date"
              value={form.end_date}
              onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              className="input-soft w-full"
            />
          </Field>
        </div>
        <Field label={t("health.field.consent")}>
          <textarea
            data-testid="health-add-med-consent"
            required
            minLength={2}
            maxLength={300}
            value={form.consent_text}
            onChange={(e) => setForm({ ...form, consent_text: e.target.value })}
            className="input-soft w-full"
            rows={2}
          />
          <div className="text-xs text-[#8A9984] mt-1 italic">
            {t("health.consent_hint")}
          </div>
        </Field>
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="health-add-med-cancel"
            className="btn-ghost"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            data-testid="health-add-med-save"
            disabled={busy}
            className="btn-primary"
          >
            {t("common.save")}
          </button>
        </div>
      </form>
    </div>
  );
}

function AddEventModal({ childId, onClose, onSaved }) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    kind: "illness",
    summary: "",
    body: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/children/${childId}/health-events`, {
        kind: form.kind,
        summary: form.summary,
        body: form.body || null,
      });
      toast.success(t("common.save"));
      await onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      data-testid="health-add-event-modal"
      className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-end sm:items-center justify-center px-4"
    >
      <form
        onSubmit={submit}
        className="bg-white rounded-3xl w-full max-w-md p-6 space-y-3"
      >
        <h3 className="font-serif text-2xl text-[#171717]">{t("health.add_event")}</h3>
        <Field label="Kind">
          <select
            data-testid="health-add-event-kind"
            value={form.kind}
            onChange={(e) => setForm({ ...form, kind: e.target.value })}
            className="input-soft w-full"
          >
            <option value="illness">Illness</option>
            <option value="allergy_note">Allergy note</option>
            <option value="first_aid">First aid</option>
            <option value="medication">Medication note</option>
          </select>
        </Field>
        <Field label="Summary">
          <input
            data-testid="health-add-event-summary"
            required
            maxLength={240}
            value={form.summary}
            onChange={(e) => setForm({ ...form, summary: e.target.value })}
            className="input-soft w-full"
          />
        </Field>
        <Field label="Details">
          <textarea
            data-testid="health-add-event-body"
            maxLength={800}
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            className="input-soft w-full"
            rows={3}
          />
        </Field>
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="health-add-event-cancel"
            className="btn-ghost"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            data-testid="health-add-event-save"
            disabled={busy}
            className="btn-primary"
          >
            {t("common.save")}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wide text-[#6A6863] mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
